import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import { ASSIGNABLE_ROLES, KNOWN_FEATURES } from '../../auth/roles';
import { IrmDataProvider } from '../../providers/types';
import { AdminInviteResult, InviteUserPayload } from '../../types/types';
import { CredentialsModal, Credentials } from './CredentialsModal';

export const InviteUserDialog = ({
    open,
    onClose,
}: {
    open: boolean;
    onClose: () => void;
}) => {
    const dp = useDataProvider<IrmDataProvider>();
    const notify = useNotify();
    const refresh = useRefresh();
    const [credentials, setCredentials] = useState<Credentials | null>(null);

    const { register, handleSubmit, control, reset, formState: { isValid } } =
        useForm<InviteUserPayload>({
            mode: 'onChange',
            defaultValues: {
                email: '',
                role: 'user',
                allowed_features: [],
                first_name: '',
                last_name: '',
                gender: 'male',
            },
        });

    const { mutate, isPending } = useMutation({
        mutationFn: (payload: InviteUserPayload) => dp.adminInviteUser(payload),
        onSuccess: (res: AdminInviteResult) => {
            notify('Invite created.');
            setCredentials({ email: res.email, tempPassword: res.tempPassword });
            reset();
            onClose();
            refresh();
        },
        onError: (e: any) =>
            notify(e?.message || 'Invite failed.', { type: 'error' }),
    });

    const onSubmit: SubmitHandler<InviteUserPayload> = data => mutate(data);

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>Invite user</DialogTitle>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField
                                label="First name"
                                required
                                fullWidth
                                inputProps={{ maxLength: 80 }}
                                {...register('first_name', { required: true, minLength: 1 })}
                            />
                            <TextField
                                label="Last name"
                                required
                                fullWidth
                                inputProps={{ maxLength: 80 }}
                                {...register('last_name', { required: true, minLength: 1 })}
                            />
                            <Controller
                                control={control}
                                name="gender"
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <InputLabel>Gender</InputLabel>
                                        <Select label="Gender" {...field}>
                                            <MenuItem value="male">Male</MenuItem>
                                            <MenuItem value="female">Female</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            />
                            <TextField
                                label="Email"
                                type="email"
                                required
                                fullWidth
                                {...register('email', { required: true })}
                            />
                            <Controller
                                control={control}
                                name="role"
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <FormControl fullWidth>
                                        <InputLabel>Role</InputLabel>
                                        <Select label="Role" {...field}>
                                            {ASSIGNABLE_ROLES.map(r => (
                                                <MenuItem key={r} value={r}>
                                                    {r}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            />
                            <Controller
                                control={control}
                                name="allowed_features"
                                render={({ field }) => (
                                    <FormControl component="fieldset">
                                        <FormGroup>
                                            {KNOWN_FEATURES.map(p => (
                                                <FormControlLabel
                                                    key={p}
                                                    label={p}
                                                    control={
                                                        <Checkbox
                                                            checked={field.value.includes(p)}
                                                            onChange={e => {
                                                                const next = new Set(field.value);
                                                                if (e.target.checked) next.add(p);
                                                                else next.delete(p);
                                                                field.onChange([...next]);
                                                            }}
                                                        />
                                                    }
                                                />
                                            ))}
                                        </FormGroup>
                                        <FormHelperText>
                                            Tabs covered by the selected role are always shown; check here to grant extra tabs.
                                        </FormHelperText>
                                    </FormControl>
                                )}
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={!isValid || isPending}
                        >
                            Create invite
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
            <CredentialsModal
                open={credentials !== null}
                onClose={() => setCredentials(null)}
                credentials={credentials}
            />
        </>
    );
};
